import { useState, useEffect, useCallback, useRef } from 'react';
import { apiDelete, apiGet, apiPost, apiPut, getToken } from '../api';

function localNow() {
  var d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + 'T' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0');
}

export function useCollectionApi(collectionName) {
  const opts = arguments.length > 1 && arguments[1] ? arguments[1] : {};
  const enabled = opts.enabled !== false;
  const pollMs = typeof opts.pollMs === 'number' ? opts.pollMs : 30000;
  const initialDelayMs = typeof opts.initialDelayMs === 'number' ? opts.initialDelayMs : 0;
  const pauseWhenHidden = opts.pauseWhenHidden !== false;
  const paged = opts.paged === true;
  const pageSize = typeof opts.pageSize === 'number' ? Math.max(1, Math.min(opts.pageSize, 1000)) : 500;
  const loadAllPages = opts.loadAllPages !== false;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const timerRef = useRef(null);
  const startupTimerRef = useRef(null);

  const fetchPage = useCallback(async function(page) {
    var p = Math.max(1, Number(page) || 1);
    return apiGet('/api/collections/' + collectionName + '?page=' + p + '&limit=' + pageSize);
  }, [collectionName, pageSize]);

  const mergeFirstPage = useCallback(function(firstPageItems) {
    var front = firstPageItems || [];
    var ids = new Set(front.map(function(x) { return x && x._id; }).filter(Boolean));
    setData(function(prev) {
      var rest = (prev || []).filter(function(x) { return !ids.has(x && x._id); });
      return front.concat(rest);
    });
  }, []);

  const isDocumentHidden = function() {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  };

  const load = useCallback(async () => {
    if (!enabled) return;
    if (!getToken()) return;
    try {
      if (!paged) {
        const res = await apiGet('/api/collections/' + collectionName);
        setData(res.items || []);
        setCurrentPage(1);
        setTotal((res.items || []).length);
        setHasMore(false);
        return;
      }

      const firstRes = await fetchPage(1);
      var firstItems = firstRes.items || [];
      setData(firstItems);
      setCurrentPage(firstRes.page || 1);
      setTotal(firstRes.total || firstItems.length);
      setHasMore(!!firstRes.hasMore);

      // Keep first paint fast, then fetch remaining pages progressively.
      if (loadAllPages && firstRes.hasMore) {
        var nextPage = (firstRes.page || 1) + 1;
        var nextHasMore = true;
        var remainingItems = [];
        while (nextHasMore) {
          const nextRes = await fetchPage(nextPage);
          var nextItems = nextRes.items || [];
          remainingItems = remainingItems.concat(nextItems);
          setCurrentPage(nextRes.page || nextPage);
          setTotal(nextRes.total || 0);
          nextHasMore = !!nextRes.hasMore;
          setHasMore(nextHasMore);
          nextPage += 1;
        }
        if (remainingItems.length) {
          setData(function(prev) { return (prev || []).concat(remainingItems); });
        }
      }
    } catch (err) {
      console.error('Load error:', err);
    }
  }, [collectionName, enabled, paged, fetchPage, loadAllPages]);

  const loadMore = useCallback(async function() {
    if (!paged) return;
    if (!enabled || !hasMore || loadingMore) return;
    if (!getToken()) return;
    setLoadingMore(true);
    try {
      var nextPage = currentPage + 1;
      const res = await fetchPage(nextPage);
      var rows = res.items || [];
      setData(function(prev) { return (prev || []).concat(rows); });
      setCurrentPage(res.page || nextPage);
      setTotal(res.total || 0);
      setHasMore(!!res.hasMore);
    } catch (err) {
      console.error('Load more error:', err);
    }
    setLoadingMore(false);
  }, [paged, enabled, hasMore, loadingMore, currentPage, fetchPage]);

  useEffect(() => {
    var mounted = true;

    if (!enabled) {
      setLoading(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (startupTimerRef.current) {
        clearTimeout(startupTimerRef.current);
        startupTimerRef.current = null;
      }
      return function() {
        mounted = false;
      };
    }

    if (!getToken()) {
      setLoading(false);
      return function() {
        mounted = false;
      };
    }

    setLoading(true);
    var startLoad = function() {
      if (pauseWhenHidden && isDocumentHidden()) {
        if (mounted) setLoading(false);
        return;
      }
      load()
        .catch(function(err) {
          console.error('Initial load error:', err);
        })
        .finally(function() {
          if (mounted) setLoading(false);
        });
    };

    if (initialDelayMs > 0) {
      startupTimerRef.current = setTimeout(startLoad, initialDelayMs);
    } else {
      startLoad();
    }

    if (pollMs > 0) {
      timerRef.current = setInterval(function() {
        if (pauseWhenHidden && isDocumentHidden()) return;
        if (paged) {
          fetchPage(1).then(function(res) {
            mergeFirstPage(res.items || []);
            setCurrentPage(function(prev) { return Math.max(prev, res.page || 1); });
            setTotal(res.total || 0);
            setHasMore(!!res.hasMore);
          }).catch(function(err) {
            console.error('Polling load error:', err);
          });
          return;
        }
        load().catch(function(err) {
          console.error('Polling load error:', err);
        });
      }, pollMs);

      if (pauseWhenHidden && typeof document !== 'undefined') {
        var onVisibilityChange = function() {
          if (!mounted) return;
          if (document.visibilityState !== 'visible') return;
          if (paged) {
            fetchPage(1).then(function(res) {
              mergeFirstPage(res.items || []);
              setCurrentPage(function(prev) { return Math.max(prev, res.page || 1); });
              setTotal(res.total || 0);
              setHasMore(!!res.hasMore);
            }).catch(function(err) {
              console.error('Visibility refresh load error:', err);
            });
            return;
          }
          load().catch(function(err) {
            console.error('Visibility refresh load error:', err);
          });
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return function() {
          mounted = false;
          if (timerRef.current) clearInterval(timerRef.current);
          if (startupTimerRef.current) clearTimeout(startupTimerRef.current);
          document.removeEventListener('visibilitychange', onVisibilityChange);
        };
      }
    }

    return function() {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (startupTimerRef.current) clearTimeout(startupTimerRef.current);
    };
  }, [collectionName, load, enabled, pollMs, initialDelayMs, pauseWhenHidden, paged, fetchPage, mergeFirstPage]);

  const addItem = useCallback(async (item) => {
    const payload = {
      ...item,
      createdAt: localNow(),
    };
    const res = await apiPost('/api/collections/' + collectionName, payload);
    const created = res.item || payload;
    setData(function(prev) { return [created].concat(prev); });
    return created;
  }, [collectionName]);

  const updateItem = useCallback(async (id, updates) => {
    await apiPut('/api/collections/' + collectionName + '/' + id, {
      ...updates,
      updatedAt: localNow(),
    });
    setData(prev => prev.map(d => d._id === id ? { ...d, ...updates } : d));
  }, [collectionName]);

  const deleteItem = useCallback(async (id) => {
    await apiDelete('/api/collections/' + collectionName + '/' + id);
    setData(prev => prev.filter(d => d._id !== id));
  }, [collectionName]);

  const bulkUpload = useCallback(async (items) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += 300) {
      chunks.push(items.slice(i, i + 300));
    }
    for (const chunk of chunks) {
      try {
        await apiPost('/api/collections/' + collectionName + '/bulk', { items: chunk });
      } catch (err) {
        console.error('Bulk upload error:', err);
        throw err;
      }
    }
    await load();
  }, [collectionName, load]);

  const replaceAll = useCallback(async (items) => {
    await apiPost('/api/collections/' + collectionName + '/replace', { items: items || [] });
    await load();
  }, [collectionName, load]);

  return {
    data,
    loading,
    loadingMore,
    currentPage,
    total,
    hasMore,
    addItem,
    updateItem,
    deleteItem,
    bulkUpload,
    replaceAll,
    loadMore,
    reload: load,
  };
}